import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { MapPin, HelpCircle, FileText, Eye, TrendingUp, Loader2 } from 'lucide-react';

export default function Dashboard() {
  const { data: site } = useQuery({
    queryKey: ['site'],
    queryFn: api.getSite
  });

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: api.getLocations
  });

  const { data: questions } = useQuery({
    queryKey: ['questions'],
    queryFn: () => api.getQuestions({ limit: 1000 })
  });

  const { data: answers } = useQuery({
    queryKey: ['answers'],
    queryFn: () => api.getAnswers({ limit: 1000 })
  });

  const { data: analytics } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => api.getAnalytics()
  });

  const stats = [
    {
      name: 'Locations',
      value: locations?.length || 0,
      icon: MapPin,
      color: 'bg-blue-500'
    },
    {
      name: 'Questions',
      value: Array.isArray(questions) ? questions.length : 0,
      icon: HelpCircle,
      color: 'bg-purple-500'
    },
    {
      name: 'Published Answers',
      value: Array.isArray(answers) ? answers.filter((a: any) => a.status === 'published').length : 0,
      icon: FileText,
      color: 'bg-green-500'
    },
    {
      name: 'Total Views',
      value: Array.isArray(analytics) ? analytics.reduce((sum: number, a: any) => sum + (a.total_views || 0), 0) : 0,
      icon: Eye,
      color: 'bg-orange-500'
    }
  ];

  const pendingQuestions = Array.isArray(questions)
    ? questions.filter((q: any) => q.status === 'pending').slice(0, 5)
    : [];

  const draftAnswers = Array.isArray(answers)
    ? answers.filter((a: any) => a.status === 'draft').slice(0, 5)
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">
          Welcome to {site?.name || 'your Answer Nodes dashboard'}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card flex items-center space-x-4">
            <div className={`${stat.color} p-3 rounded-lg`}>
              <stat.icon className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{stat.name}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => window.location.href = '/locations'}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
          >
            <MapPin className="h-4 w-4 mr-2" />
            Add Location
          </button>
          <button
            onClick={() => window.location.href = '/questions'}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Generate Questions
          </button>
          <button
            onClick={() => window.location.href = '/answers'}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium bg-white text-gray-700 hover:bg-gray-50"
          >
            <FileText className="h-4 w-4 mr-2" />
            Generate Answers
          </button>
        </div>
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Questions */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Pending Questions</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">{pendingQuestions.length} pending</span>
          </div>
          {pendingQuestions.length > 0 ? (
            <ul className="space-y-3">
              {pendingQuestions.map((q: any) => (
                <li key={q.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <HelpCircle className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">{q.question_text}</p>
                    <p className="text-xs text-gray-500">Priority: {q.priority_score}</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No pending questions</p>
          )}
        </div>

        {/* Draft Answers */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Draft Answers</h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{draftAnswers.length} drafts</span>
          </div>
          {draftAnswers.length > 0 ? (
            <ul className="space-y-3">
              {draftAnswers.map((a: any) => (
                <li key={a.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <FileText className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 truncate">
                      {a.content?.meta?.title || 'Untitled'}
                    </p>
                    <p className="text-xs text-gray-500">{a.word_count} words</p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 text-center py-4">No draft answers</p>
          )}
        </div>
      </div>

      {/* Site Info */}
      {site && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Site Information</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Business Type</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">
                {site.business_type?.replace(/-/g, ' ')}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Domain</dt>
              <dd className="text-sm font-medium text-gray-900">{site.domain || 'Not set'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Content Tone</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">
                {site.settings?.content_tone}
              </dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Language</dt>
              <dd className="text-sm font-medium text-gray-900">{site.settings?.language}</dd>
            </div>
          </dl>
        </div>
      )}
    </div>
  );
}
